import React from "react"
import trashSVG from "../../../priv/static/svg/generic/trash-2.svg"
import SVG from "react-inlinesvg"
import { observer } from "mobx-react"
import { Question } from "../models/Question"
import FormLabel from "../legacy_components/shared/FormLabel"
import SwitchInput from "./SwitchInput"
import webify from "../util/webify"

interface State {
  question: Question
}

interface PossibleAnswersFormProps {
  question: Question
  display: boolean
}

const PossibleAnswersForm = observer(({ display, question }: PossibleAnswersFormProps) => {
  return (
    <div className={display ? "grid" : "hidden"}>
      {question.possibleAnswers.items.length > 0 && <FormLabel required={true} htmlFor="transaction_type" label="Answers" />}
      <div className="grid gap-block-4">
        {question.possibleAnswers.items.map((pa, index) => (
          <div
            className="grid items-center grid-cols-12 gap-block-4 gap-inline-4"
            data-role="possible-answer-row"
            key={index}
            style={{ display: pa.delete ? "none" : "grid" }}
          >
            <div className="col-span-8">
              {pa.id ? (
                <input
                  type="hidden"
                  id={`question_possible_answers_${index}_id`}
                  name={`question[possible_answers][${index}][id]`}
                  value={pa.id}
                />
              ) : (
                ""
              )}
              {pa.id && pa.delete ? (
                <input
                  id={`question_possible_answers_${index}_delete`}
                  name={`question[possible_answers][${index}][delete]`}
                  type="hidden"
                  value={pa.delete && "true"}
                />
              ) : (
                ""
              )}
              <input
                className=""
                data-role="answer-input"
                id={`question_possible_answers_${index}_name`}
                name={`question[possible_answers][${index}][name]`}
                placeholder="New Answer"
                type="text"
                value={pa.name}
                onChange={(e) => (pa.name = e.target.value)}
              />
            </div>

            <div className="col-span-1">
              <a className="text-gray-800" data-role="delete-button" onClick={() => (pa.delete = true)}>
                <SVG src={trashSVG} />
              </a>
            </div>
            <div className="col-span-1" />
          </div>
        ))}

        <div className="grid justify-start">
          <a
            className="border-gray-200 button secondary"
            data-role="add-new-answer"
            onClick={question.possibleAnswers.pushNewPossibleAnswer}
          >
            Add Another Possible Answer
          </a>
        </div>
      </div>
    </div>
  )
})

@observer
export default class AdminQuestionForm extends React.Component<any, State> {
  constructor(props) {
    super(props)

    this.state = {
      question: new Question(props.question),
    }
  }

  render() {
    const { question } = this.state

    const deleteURL = `/admin/questionnaires/${question.questionnaireId}/questions/${question.id}`

    return (
      <div className="form-grid-setup">
        <div>
          <FormLabel required={true} htmlFor="question_label" label="Label" />
          <input
            className="form-control"
            id="question_label"
            name="question[label]"
            placeholder="What is your T-Shirt size?"
            type="text"
            value={question.label}
            required={true}
            onChange={(e) => (question.label = e.target.value)}
          />
        </div>

        <div>
          <FormLabel required={false} htmlFor="question_description" label="Description" />
          <input
            id="question_description"
            name="question[description]"
            placeholder="An optional detailed description of what you are asking"
            type="text"
            value={question.description ? question.description.replace(/\s+/g, " ") : ""}
            onChange={(e) => (question.description = e.target.value)}
          ></input>
        </div>

        <label className="checkbox-list">
          <input type="hidden" value="true" name="question[optional?]" />
          <input
            type="checkbox"
            checked={!question.optional}
            value="false"
            name="question[optional?]"
            onChange={() => {
              question.optional = !question.optional
            }}
          />
          Answer Is Required
        </label>

        <div>
          <div className="mb-2">
            <FormLabel required={true} htmlFor="question_type" label="Question Type" />
          </div>

          <div className="">
            <div className="flex py-2">
              <input
                className="flex-none form-control"
                id="question_type_single_choice"
                name={`question[type]`}
                type="radio"
                value="single_choice"
                disabled={question.hasGivenAnswers}
                checked={question.type === "single_choice"}
                onChange={question.setSingleChoice}
                data-role="question-type-single-choice"
              />

              <span className="">
                <label htmlFor="question_type_single_choice">
                  <span className="ml-1 font-semibold">Single Selection: </span>
                  <span className="text-gray-700">Passengers must choose one of many options</span>
                </label>
              </span>
            </div>

            <div className="flex py-2">
              <input
                className="flex-none form-control"
                id="question_type_multi_choice"
                name={`question[type]`}
                type="radio"
                value="multi_choice"
                disabled={question.hasGivenAnswers}
                checked={question.type === "multi_choice"}
                onChange={question.setMultiChoice}
                data-role="question-type-multi-choice"
              />

              <span className="">
                <label htmlFor="question_type_multi_choice">
                  <span className="ml-1 font-semibold">Multi Selection: </span>
                  <span className="text-gray-700">Passengers must choose at least one of many options</span>
                </label>
              </span>
            </div>

            <div className="flex py-2">
              <input
                className="flex-none form-control"
                id="question_type_text_field"
                name={`question[type]`}
                type="radio"
                value="text_field"
                disabled={question.hasGivenAnswers}
                checked={question.type === "text_field"}
                onChange={question.setTextField}
                data-role="question-type-text-field"
              />

              <span className="">
                <label htmlFor="question_type_text_field">
                  <span className="ml-1 font-semibold">Text Field: </span>
                  <span className="text-gray-700 input-notice">Passengers must type their answer out on a single line</span>
                </label>
              </span>
            </div>

            <div className="flex py-2">
              <input
                className="flex-none form-control"
                id="question_type_text_area"
                name={`question[type]`}
                type="radio"
                value="text_area"
                checked={question.type === "text_area"}
                disabled={question.hasGivenAnswers}
                onChange={question.setTextArea}
                data-role="question-type-text-area"
              />

              <span className="">
                <label htmlFor="question_type_text_area">
                  <span className="ml-1 font-semibold">Text Area: </span>
                  <span className="text-gray-700 input-notice">
                    Passengers are able to type their answer out on multiple lines; answers required
                  </span>
                </label>
              </span>
            </div>

            <div className="flex py-2">
              <input
                className="flex-none form-control"
                id="question_type_file_upload"
                name={`question[type]`}
                type="radio"
                value="file_upload"
                checked={question.type === "file_upload"}
                disabled={question.hasGivenAnswers}
                onChange={question.setFileUpload}
                data-role="question-type-file-upload"
              />

              <span className="">
                <label htmlFor="question_type_file_upload">
                  <span className="ml-1 font-semibold">File upload: </span>
                  <span className="text-gray-700 input-notice">Passengers are able to upload a file</span>
                </label>
              </span>
            </div>
          </div>
        </div>

        <PossibleAnswersForm display={question.hasPossibleAnswers} {...this.state} />

        <div className="button-row">
          <button type="submit" className="button" data-role="submit">
            {question.hasPossibleAnswers ? "Save Question and Answers" : "Save"}
          </button>

          {question.id && this.props.canEditTrips && (
            <a
              className="button delete-button"
              data-confirm="Are you sure?"
              data-csrf={this.props.csrf}
              data-method="delete"
              data-role="delete"
              data-to={deleteURL}
              href={deleteURL}
              rel="nofollow"
            >
              Delete
            </a>
          )}
        </div>
      </div>
    )
  }
}

webify("admin-question-form", AdminQuestionForm)
